import Controller from '@ember/controller'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { isWithinNorthAmericanSummer } from 'teacher-ui-client/utils/dates'
import type CatchUpGuidesRoute from 'teacher-ui-client/subscription-type/catch-up-guides/route'
import type { IntlService } from 'ember-intl'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import { isPresent } from '@ember/utils'

interface GradeButton {
  text: string
  value: number | string
}

export default class extends Controller<ModelFor<CatchUpGuidesRoute>> {
  @service intl!: IntlService
  @service gradeSets!: GradeSetsService

  @tracked
  currentlyFilteredGradePosition: number | string = 'all'

  get guides() {
    return this._getGuidesViewModel(this.model.catchUpGuides)
  }

  get title() {
    return isWithinNorthAmericanSummer(new Date())
      ? this.intl.t('catchUpGuides.readingTitleSummer')
      : this.intl.t('catchUpGuides.readingTitle')
  }

  get gradePositionFilterButtons(): GradeButton[] {
    // There's only books for these grades
    const allowedGradePositions = [2, 3, 4, 5]
    const gradeButtons = this.gradeSets.grades.reduce<GradeButton[]>((buttons, grade) => {
      return allowedGradePositions.includes(grade.position)
        ? [...buttons, { text: grade.fullName, value: grade.position }]
        : buttons
    }, [])
    return [{ text: this.intl.t('all'), value: 'all' }, ...gradeButtons]
  }

  _getGuidesViewModel(contentfulData: (typeof this)['model']['catchUpGuides']) {
    return contentfulData
      .map((entry) => {
        if (!entry.fields.cover?.fields?.file?.url || !entry.fields.file?.fields?.file?.url) return null
        return {
          title: entry.fields.title,
          description: entry.fields.description,
          subscriptionType: entry.fields.subscriptionType,
          coverUrl: entry.fields.cover.fields.file.url,
          fileUrl: entry.fields.file.fields.file.url,
          gradePosition: entry.fields.gradePosition,
        }
      })
      .filter(isPresent)
  }

  get currentlyVisibleGuides() {
    const { guides, currentlyFilteredGradePosition } = this

    if (currentlyFilteredGradePosition === 'all') {
      return guides
    } else {
      return guides.filter((guide) => guide?.gradePosition === currentlyFilteredGradePosition)
    }
  }

  @action
  filterByGradePosition(grade: number | string) {
    this.currentlyFilteredGradePosition = grade
  }
}
